import * as dialogs from 'frontend/utils/dialogs';
import {
  GLOBAL_EVENT,
  GLOBAL_EVENT_TYPES,
  ROOM_CHANNEL_MESSAGE_TYPES,
  SESSION_EVENTS_TYPES,
  SHAREABLE_ENTITIES,
} from 'frontend/constants';
import { HOST_CONTROLS } from 'frontend/constants/settings';
import { dependentKeyCompat } from '@ember/object/compat';
import { equal, reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import Service, { service } from '@ember/service';
import moment from 'moment-timezone';

export default class AppointmentService extends Service {
  @service('chime.meeting-manager') meetingManager;
  @service('chime.remote-videos') remoteVideos;
  @service('chime.local-audio-video') localAudioVideo;
  @service('chime.roster') rosterService;
  @service('chime.data-messaging') dataMessaging;
  @service('chime.sdk') chimeSdk;
  @service appointmentSettings;
  @service audioVideoAdapter;
  @service chat;
  @service floatingUiElements;
  @service persistentProperties;
  @service roomChannel;
  @service router;
  @service session;
  @service uiElements;
  @service whiteboard;

  @tracked shareAttendeeId = null;
  @tracked sharedEntity = null;

  startSharingTime = null;

  @reads('session.roomModel') roomModel;
  @reads('session.isHost') isHost;
  @reads('roomModel.featureThClinicianAuth') featureThClinicianAuth;
  @reads('meetingManager.localAttendeeId') localAttendeeId;
  @dependentKeyCompat @reads('meetingManager.isConnected') isConnected;
  @reads('rosterService.remoteAttendeeIds') remoteAttendeeIds;
  @reads('rosterService.size') rosterSize;
  @reads('rosterService.activeSpeakerId') activeSpeakerId;
  @reads('localAudioVideo.tileId') localTileId;
  @reads('persistentProperties.selectedVideoDeviceId') selectedVideoDeviceId;
  @reads('persistentProperties.selectedAudioInputDeviceId') selectedAudioInputDeviceId;
  @reads('persistentProperties.selectedAudioOutputDeviceId') selectedAudioOutputDeviceId;
  @reads('persistentProperties.selectedVideoQuality') selectedVideoQuality;
  @reads('uiElements.pinnedAttendeeId') pinnedAttendeeId;
  @equal('sharedEntity', SHAREABLE_ENTITIES.whiteboard) isWhiteboardShared;
  @equal('sharedEntity', SHAREABLE_ENTITIES.screen) isScreenShared;

  get mainAttendeeId() {
    let [firstRemoteAttendeeId] = this.remoteAttendeeIds;

    return this.pinnedAttendeeId || this.activeSpeakerId || firstRemoteAttendeeId;
  }

  get hasMainView() {
    return !!(this.mainAttendeeId || this.sharedEntity);
  }

  @dependentKeyCompat
  get isSharing() {
    return !!this.sharedEntity && this.shareAttendeeId === this.localAttendeeId;
  }

  async loadSdk() {
    await this.chimeSdk.load();
  }

  get isSharingWhiteboard() {
    return this.isSharing && this.isWhiteboardShared;
  }

  get isSharingScreen() {
    return this.isSharing && this.isScreenShared;
  }

  toggleWhiteboardSharing() {
    this.isSharingWhiteboard ? this.#stopSharingWhiteboard() : this.#startSharingWhiteboard();
  }

  toggleScreenSharing() {
    // noop
  }

  toggleWhiteboardLocked() {
    let isWhiteboardLocked = this.whiteboard.isLocked;

    if (this.isSharing) {
      this.dataMessaging.sendMessage(
        {
          type: isWhiteboardLocked
            ? GLOBAL_EVENT_TYPES.unlockWhiteboard
            : GLOBAL_EVENT_TYPES.lockWhiteboard,
        },
        GLOBAL_EVENT
      );
    }
    this.whiteboard.setInitialData({ isLocked: !isWhiteboardLocked });
  }

  getAttendee(attendeeId) {
    return this.localAttendeeId === attendeeId
      ? this.session.userData
      : this.rosterService.getAttendee(attendeeId);
  }

  initSession() {
    let { meeting, attendee } = this.roomModel;
    let { selectedVideoDeviceId, selectedAudioInputDeviceId, selectedAudioOutputDeviceId } = this;
    this.session.resetRoomProperties();
    this.session.setInWaitingRoom(!this.isHost);

    return this.meetingManager.initMeetingSession(
      { meeting, attendee },
      {
        deviceLabels: this.audioVideoAdapter.deviceLabels,
        devices: { selectedVideoDeviceId, selectedAudioInputDeviceId, selectedAudioOutputDeviceId },
        videoQuality: this.selectedVideoQuality,
      }
    );
  }

  async join() {
    if (!this.meetingManager.audioVideo) {
      await this.initSession();
    }

    let signalingOnly = !this.session.isHost && this.featureThClinicianAuth;

    await this.#startChimeMeeting({ signalingOnly });

    this.#subscribeToRoomChannel();
    this.#subscribeToDataMessaging();
    window.addEventListener('beforeunload', () => {
      this.session.processEnding();
    });

    if (!this.isHost) return;

    this.session.setHostJoinTime(this.roomModel.hostJoinTime || moment());
  }

  async leave(options = {}) {
    let { endCall = true, hostLeft = false } = options;

    if (endCall) {
      this.session.processEnding({ hostLeft });
    }

    this.#resetProperties();
    await this.meetingManager.leave();
    this.floatingUiElements.hideJoinRequestNotification();
    this.chat.resetChatProperties();
  }

  sendToWaitingRoom(attendeeId) {
    this.session.sendToWaitingRoom(this.rosterService.roster.get(attendeeId));
  }

  #startSharingWhiteboard() {
    if (this.shareParticipant) {
      dialogs.cannotStartSharing();
      return;
    }

    this.uiElements.handleSharing();
    this.sharedEntity = SHAREABLE_ENTITIES.whiteboard;
    this.shareAttendeeId = this.localAttendeeId;
    this.startSharingTime = moment();

    this.dataMessaging.sendMessage(
      { type: GLOBAL_EVENT_TYPES.startWhiteboard, isWhiteboardLocked: this.whiteboard.isLocked },
      GLOBAL_EVENT
    );
  }

  async #stopSharingWhiteboard({ ensure } = { ensure: true }) {
    if (ensure) {
      let { dismiss } = await dialogs.ensureStopWhiteboardSharing();
      if (dismiss) return;
    }

    this.dataMessaging.sendMessage({ type: GLOBAL_EVENT_TYPES.stopWhiteboard }, GLOBAL_EVENT);
    this.#clearSharingProperties();

    if (!this.isSharing) return;
    this.mixpanel.track('whiteboard stopped');
  }

  #stopSharingScreen() {
    // noop
  }

  #subscribeToDataMessaging() {
    this.dataMessaging.addMessageHandler(GLOBAL_EVENT, messageProperties => {
      switch (messageProperties.message.type) {
        case GLOBAL_EVENT_TYPES.startWhiteboard:
          return this.#handleRemoteWhiteboardStart(messageProperties);
        case GLOBAL_EVENT_TYPES.stopWhiteboard:
          return this.#clearSharingProperties();
        case GLOBAL_EVENT_TYPES.lockWhiteboard:
        case GLOBAL_EVENT_TYPES.unlockWhiteboard:
          return this.toggleWhiteboardLocked();
      }
    });
  }

  async #handleRemoteWhiteboardStart({ message, senderAttendeeId, timestamp }) {
    if (this.isSharing) {
      if (!moment(timestamp).isBefore(this.startSharingTime)) return;

      this.isSharingScreen
        ? await this.toggleScreenSharing()
        : await this.toggleWhiteboardSharing();
      dialogs.cannotStartSharing();
    }

    this.sharedEntity = SHAREABLE_ENTITIES.whiteboard;
    this.shareAttendeeId = senderAttendeeId;
    this.whiteboard.setInitialData({
      state: message.fileData?.fileBase64,
      isLocked: message.isWhiteboardLocked,
    });
    this.uiElements.handleSharing();
  }

  #resetProperties() {
    this.session.setInWaitingRoom(!this.isHost);
    this.isGridView = false;
    this.sharedEntity = null;
    this.shareAttendeeId = null;
  }

  async #startChimeMeeting({ signalingOnly } = {}) {
    await this.meetingManager.start({ signalingOnly });
  }

  #subscribeToRoomChannel() {
    if (!this.featureThClinicianAuth || this.roomChannel.connected) return;

    this.roomChannel.subscribe({
      roomId: this.roomModel.roomId,
      userName: this.persistentProperties.userName,
    });
    this.roomChannel.addMessageHandler(ROOM_CHANNEL_MESSAGE_TYPES.session, properties => {
      let { message, participantSid: senderUuid } = properties;

      let handler = {
        [SESSION_EVENTS_TYPES.admitJoin]: this.#handleJoinAdmit,
        [SESSION_EVENTS_TYPES.declineJoin]: this.#handleJoinDecline,
        [SESSION_EVENTS_TYPES.ended]: this.#handleSessionEnd,
        [SESSION_EVENTS_TYPES.hostControlsChanged]: this.#handleHostControlsChanged,
        [SESSION_EVENTS_TYPES.joinRequest]: this.#handleJoinRequest,
        [SESSION_EVENTS_TYPES.participantUnsubscribed]: this.#handleParticipantUnsubscribed,
        [SESSION_EVENTS_TYPES.sendToWaitingRoom]: this.#handleSendingToWaitingRoom,
        [SESSION_EVENTS_TYPES.shareUserData]: this.#handleSharedUserData,
      }[message.eventType];

      handler?.call(this, { message, senderUuid });
    });
  }

  async #handleJoinAdmit() {
    if (!this.session.inWaitingRoom) return;

    this.session.setInWaitingRoom(false);
    await this.#startChimeMeeting();
  }

  #handleJoinDecline() {
    this.session.handleJoinRequestDeclined();
    this.roomChannel.unsubscribe();
  }

  #handleJoinRequest({ message }) {
    this.session.handleNewRequests(message.requests);
  }

  async #handleSessionEnd({ senderUuid } = {}) {
    if (this.persistentProperties.uuid === senderUuid) return;

    this.leave({ hostLeft: true });
    await dialogs.appointmentEnded();

    this.router.transitionTo('appointment.ended');
  }

  #handleSharedUserData({ message, senderUuid: participantSid }) {
    this.rosterService.setAttendeeUserData(participantSid, message.userData);
  }

  async #handleSendingToWaitingRoom() {
    let observer = {
      audioVideoDidStop: async () => {
        await waitForRender();
        this.initSession();
      },
    };
    // eslint-disable-next-line ember/no-observers
    this.meetingManager.audioVideo?.addObserver(observer);
    await this.leave({ endCall: false });
    this.session.showSentToWaitingRoomNotification();
  }

  #handleParticipantUnsubscribed({ message }) {
    this.session.cancelJoinRequest(message.uuid);
  }

  #handleHostControlsChanged({ message }) {
    if (this.isHost || !message?.changedControls) return;

    let { changedControls } = message;

    Object.entries(changedControls).forEach(([control, enabled]) => {
      if (control === HOST_CONTROLS.sharingEnabled && this.isSharing && !enabled) {
        this.isSharingScreen
          ? this.#stopSharingScreen()
          : this.#stopSharingWhiteboard({ ensure: false });
      }
    });
    this.appointmentSettings.change(changedControls);
  }

  #clearSharingProperties() {
    this.sharedEntity = null;
    this.shareAttendeeId = null;
    this.whiteboard.setInitialData({ state: null, isLocked: true });
  }
}
